import React, { useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
} from "@mui/material";
import { useGetInvoiceQuery } from "api/api-invoices";
import type { PortfolioValuationData } from "investor/types/types-invoices";
import { useGetCurrentUserQuery } from "api/api-users-v2";

interface PortfolioValuationsProps {
  data?: any;
  userId?: string;
}

const PortfolioValuations: React.FC<PortfolioValuationsProps> = ({
  data,
  userId,
}) => {
  const currentYear = new Date().getFullYear(); // Get the current year
  const { data: currentUser } = useGetCurrentUserQuery();

  // Extract id from current user or use the provided userId
  const idToUse = userId || currentUser?.id;

  const [amount, setAmount] = useState<PortfolioValuationData>({
    totalPrincipalInvested: 0,
    totalInterestAccrued: 0,
  });

  // Use the data passed as prop if available, otherwise fetch it
  const { data: activeInvoice, isLoading } = useGetInvoiceQuery(
    { endpoint: `dashboard/investors-summary?active_invoices=true` },
    // {
    //   skip: !!data || !idToUse, // Skip API call if data is provided as prop or if idToUse is not available
    // }
    {
      refetchOnMountOrArgChange: true,
      skip: !idToUse, // Skip if id is not available
    }
  );

  // Fetch all invoices since inception
  const { data: allInvoicesData } = useGetInvoiceQuery(
    { endpoint: 'dashboard/investors-summary?year=all' },
    { skip: !idToUse }
  );

  // Fetch active invoices for 'Current Portfolio'
  const { data: activeInvoicesData } = useGetInvoiceQuery(
    { endpoint: 'dashboard/investors-summary?active_invoices=true' },
    { skip: !idToUse }
  );

  // Calculate Since Inception values
  const validAllInvoices = allInvoicesData?.invoices?.filter((inv: any) => {
    return inv.phase !== "0_PRE_LISTING" && 
           inv.phase !== "PRE_LISTING" && 
           inv.status !== "pre_listing" &&
           inv.status !== "prelisting";
  }) || [];
  const totalPrincipalInvested = validAllInvoices.reduce(
    (sum: number, inv: any) => sum + (inv.principle_amount || 0),
    0
  );
  const totalInterestAccrued = validAllInvoices.reduce(
    (sum: number, inv: any) => sum + (inv.interest_amount || 0),
    0
  );

  // Calculate Current Portfolio values
  const validActiveInvoices = activeInvoicesData?.invoices?.filter((inv: any) => {
    return inv.phase !== "0_PRE_LISTING" && 
           inv.phase !== "PRE_LISTING" && 
           inv.status !== "pre_listing" &&
           inv.status !== "prelisting";
  }) || [];
  const totalActiveInvoiceInvested = validActiveInvoices.reduce(
    (sum: number, inv: any) => sum + (inv.principle_amount || 0),
    0
  );
  const cashAvailable = activeInvoicesData?.total_cash_available ?? 0;
  const valueOfCurrentPortfolio = totalActiveInvoiceInvested + cashAvailable;

  const formatCurrency = (amount: number) => {
    if (isNaN(amount) || amount === 0) return "$0";
    return `$${Math.round(amount).toLocaleString()}`;
  };

  const formattedPortfolioData = [
    { type: 'header', label: 'Since Inception:' },
    { type: 'field', label: 'Total Invoice Invested', value: totalPrincipalInvested },
    { type: 'field', label: 'Total Interest Earned', value: totalInterestAccrued },
    { type: 'header', label: 'Current Portfolio:' },
    { type: 'field', label: 'Total Active Invoice Invested', value: totalActiveInvoiceInvested },
    { type: 'field', label: 'Cash Available:', value: cashAvailable },
    { type: 'field', label: 'Value of Current Portfolio', value: valueOfCurrentPortfolio },
  ];

  if (!idToUse && !data) {
    return (
      <Card
        sx={{
          backgroundColor: "#f0f0f0",
          padding: 2,
          height: "100%",
          boxShadow: "0px 3px 6px rgba(0, 0, 0, 0.1)",
          borderRadius: 2,
        }}
      >
        <CardContent sx={{ padding: 0 }}>
          <Typography
            variant="h4"
            gutterBottom
            sx={{ fontWeight: "bold", mb: 2 }}
          >
            Portfolio Valuation {currentYear}
          </Typography>
          <Alert severity="warning">
            No user data available. Please log in again.
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      sx={{
        backgroundColor: "#f0f0f0",
        padding: 2,
        // height: "100%",
        boxShadow: "0px 3px 6px rgba(0, 0, 0, 0.1)",
        borderRadius: 2,
      }}
    >
      <CardContent sx={{ padding: 0 }}>
        <Typography
          variant="h4"
          gutterBottom
          sx={{ fontWeight: "bold", mb: 2 }}
        >
          Portfolio Valuation {currentYear}
        </Typography>
        <TableContainer
          component={Paper}
          sx={{
            backgroundColor: "#fff",
            boxShadow: "none",
            borderRadius: 1,
            overflow: "hidden",
          }}
        >
          <Table size="small">
            <TableHead sx={{ backgroundColor: "#f5f5f5" }}></TableHead>
            <TableBody>
              {formattedPortfolioData.map((row) =>
                row.type === 'header' ? (
                  <TableRow key={`${row.label}-${row.type}`}>
                    <TableCell colSpan={2} sx={{ fontWeight: 'bold', background: 'transparent', borderBottom: 'none', pt: row.label === 'Since Inception:' ? 0 : 2, pb: 0 }}>
                      {row.label}
                    </TableCell>
                  </TableRow>
                ) : (
                  <TableRow key={`${row.label}-${row.type}`}>
                    <TableCell sx={{ fontWeight: 500 }}>{row.label}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 500 }}>
                      US$ {formatCurrency(row.value).replace('$', ' ')}
                    </TableCell>
                  </TableRow>
                )
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
};

export default PortfolioValuations;
